<template>
  <div class="group -narrow -only -bg-01 -hs-medium -vs-medium">
    <div class="-flex -vcenter -between">
      <h1 class="heading -primary">
        {{ isEdit ? 'Edit Routine' : 'Create a Routine' }}
      </h1>
      <div v-if="isEdit" class="heading-actions">
        <div class="heading-action">
          <form
            ref="deleteFormRef"
            method="POST"
            :action="`/my/routines/${routine.id}/edit-status`"
          >
            <input name="_method" type="hidden" value="PUT" />
            <input name="status" type="hidden" value="0" />
            <csrf-input />
            <delete-button
              :modal-heading="`Delete ${routine.status} Routine`"
              :modal-text="deleteText"
              modal-id="routine-delete-modal"
              label="Delete Routine"
              classes="-delete -ico-txt"
              :submitting="deleting"
              @delete-action="handleDelete"
            />
          </form>
        </div>
      </div>
    </div>
    <div class="form -full">
      <vee-form v-slot="{ validate }" as="" :initial-errors="initialErrors">
        <form
          ref="formRef"
          class="standard-form"
          method="post"
          novalidate
          @submit.prevent="checkWeekChange(validate)"
        >
          <input v-if="isEdit" name="_method" type="hidden" value="PUT" />

          <fieldset class="standard-fieldset" data-tour="routine-title">
            <legend class="heading -small">Basic Information</legend>

            <text-input
              v-model="formData.title"
              input-id="title"
              label="Title"
              placeholder="My Custom Routine"
              rules="required|max:100"
              maxlength="100"
            />

            <textarea-input
              v-model="formData.body"
              input-id="body"
              label="Description"
              placeholder="Description for custom routine"
              :large="true"
            />

            <select-input
              v-if="!isEdit || routine.status === 'Draft'"
              v-model="formData.weeks"
              input-id="weeks"
              label="Length in Weeks"
              rules="required"
              :options="weekOptions"
              validate-as="Weeks"
            />
          </fieldset>

          <form-fieldset-slot
            legend="Color"
            legend-classes="visual-options-legend"
            input-name="image_color"
            data-tour="routine-color"
          >
            <div class="visual-input-group">
              <visual-input
                v-for="color in imageColors"
                :key="color.value"
                v-model="formData.image_color"
                type="radio"
                :input-id="`color_${color.value}`"
                name="image_color"
                :label="color.value"
                rules="required"
                :checked-value="color.value"
                validate-as="Image color"
              >
                <span :class="`cover-color -${color.value}`"> </span>
              </visual-input>
            </div>
          </form-fieldset-slot>

          <form-fieldset-slot
            legend="Icon"
            legend-classes="visual-options-legend"
            input-name="image_icon"
            data-tour="routine-icon"
          >
            <div class="visual-input-group">
              <visual-input
                v-for="icon in imageIcons"
                :key="icon.value"
                v-model="formData.image_icon"
                type="radio"
                :input-id="`icon_${icon.value}`"
                name="image_icon"
                :label="icon.value"
                rules="required"
                :checked-value="icon.value"
                validate-as="Image icon"
              >
                <span>
                  <i :class="icon.iconSet">{{ icon.icon }}</i>
                </span>
              </visual-input>
            </div>
          </form-fieldset-slot>

          <div class="form-actions">
            <div class="form-action" data-tour="routine-save">
              <csrf-input />
              <submit-button
                :submitting="processing"
                label="Save and Continue"
                submitting-label="Processing…"
                classes="-main -large-medium-only"
                @submit-action="checkWeekChange(validate)"
              />
            </div>
            <div class="form-action">
              <button type="button" class="btn -link -large-medium-only" @click.prevent="cancel">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </vee-form>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useModalsStore } from '@/stores/modals'
import { Form as VeeForm } from 'vee-validate'
import { errorBagToInitialErrors } from '@/utils/forms/validation-rules'
import FormFieldsetSlot from '@/components/forms/form-fieldset-slot.vue'
import VisualInput from '@/components/forms/visual-input.vue'
import TextInput from '@/components/forms/text-input.vue'
import TextareaInput from '@/components/forms/textarea-input.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import DeleteButton from '@/components/buttons/delete-button.vue'
import SelectInput from '@/components/forms/select-input.vue'

/**
 * Routine credit and edit form (for basic information).
 */
export default defineComponent({
  name: 'RoutineInfoForm',
  components: {
    SelectInput,
    VeeForm,
    FormFieldsetSlot,
    TextInput,
    TextareaInput,
    VisualInput,
    CsrfInput,
    SubmitButton,
    DeleteButton,
  },
  props: {
    // server side validation
    errorBag: {
      type: Object,
      default: () => ({}),
    },
    initFormData: {
      type: Object,
      default: () => ({}),
    },

    // @see Plan::imageIcons
    imageIcons: {
      type: Array,
      default: () => [],
    },

    // @see Plan::imageColors
    imageColors: {
      type: Array,
      default: () => [],
    },
    // used only for edit
    routine: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      processing: false,
      weekOptions: [
        { value: '', label: 'Select One' },
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 4, label: '4' },
        { value: 8, label: '8' },
      ],
      deleting: false,
      formData: {
        // defaults
        title: '',
        body: '',
        weeks: '',
        image_color: '',
        image_icon: '',
        // edit
        ...this.routine,

        // previous form data if validation error
        ...this.initFormData,
      },
    }
  },
  computed: {
    ...mapState(useModalsStore, ['simpleModal']),

    isEdit() {
      return !!(this.routine && this.routine.id)
    },

    deleteText() {
      return this.routine.status === 'Live'
        ? "Are you sure you want to delete this routine? All 'routine complete' stats will be kept as well as past scheduled instances or in progress instances. Future scheduled instances will be removed."
        : 'Are you sure you want to delete this routine?'
    },

    modalId() {
      return 'routine-week-reduce-modal'
    },

    // only return first error per field, backend returns array
    initialErrors() {
      return errorBagToInitialErrors(this.errorBag)
    },
  },

  watch: {
    'simpleModal.action'(newAction, prevAction) {
      if (
        this.simpleModal.id === this.modalId &&
        newAction !== prevAction &&
        newAction === 'confirm'
      ) {
        this.handleSubmit()
      }
    },
  },

  // instance methods
  methods: {
    ...mapActions(useModalsStore, ['openSimpleModal']),

    openModal() {
      this.openSimpleModal({
        type: 'CONFIRM',
        id: this.modalId,
        props: {
          heading: 'Weeks have changed!',
          subheading: `Are you sure you want reduce your routine weeks from <strong>${this.routine.weeks}</strong> to <strong>${this.formData.weeks}</strong>?  Workouts will be removed after week ${this.formData.weeks} and this action cannot be undone!`,
          buttonText: 'Confirm',
        },
      })
    },

    cancel() {
      // go back to edit schedule view if on edit mode
      if (this.isEdit && this.routine.status === 'Draft') {
        return (window.location.href = `/my/routines/${this.routine.id}/edit-schedule`)
      }

      if (this.isEdit && this.routine.status === 'Live') {
        return (window.location.href = `/my/routines/${this.routine.id}`)
      }

      // otherwise go to list view
      window.location.href = `/my/routines`
    },

    // if weeks changed, open modal to confirm, otherwise submit form
    async checkWeekChange(validate) {
      // validate form before checking weeks or submitting
      const results = await validate()

      // invalid, stop
      if (!results.valid) {
        return
      }

      // check modal
      if (
        this.isEdit &&
        this.routine.status === 'Draft' &&
        this.formData.weeks < this.routine.weeks
      ) {
        this.openModal()
        return
      }

      // submit, assume valid
      return this.handleSubmit()
    },

    async handleSubmit() {
      if (this.processing) {
        return
      }

      this.processing = true

      // wait until next tick to make sure v-models update
      this.$nextTick(() => this.$refs.formRef.submit())
    },

    // create a form submission
    async handleDelete() {
      if (this.deleting) {
        return
      }

      // set both loading stats to stop users from clicking save while deleting
      this.deleting = true
      this.processing = true

      this.$nextTick(() => this.$refs.deleteFormRef.submit())
    },
  },
})
</script>
